import {
    CommandResponse,
    ExclusionSection,
    PlacementPayload,
    RenamePayload,
    RevisionedCommandResponse,
    StudyEntityType,
    StudySection,
    toExclusionSection,
} from '@faro/study-designer-model';
import axios from 'axios';
import { createStudyEntityHookFactory } from '../createStudyEntityHookFactory';
import { StudyPathParameter } from '../parameter';

const factory = createStudyEntityHookFactory<ExclusionSection>(StudyEntityType.ExclusionSection);

export const useExclusionSections = factory.list(
    (
        { studyId, ...query }: StudyPathParameter & { revision?: number },
        signal?: AbortSignal
    ): Promise<ExclusionSection[]> => {
        return axios
            .get(`/api/study-designer/studies/${studyId}/exclusion-sections`, {
                signal,
                params: query,
            })
            .then(response => (response.data ?? []).map(toExclusionSection));
    }
);

export const useExclusionSectionUpdate = factory.mutate(
    (entity: ExclusionSection): Promise<RevisionedCommandResponse<CommandResponse>> => {
        return axios
            .put(`/api/study-designer/studies/${entity.studyId}/exclusion-sections/${entity.id}`, entity)
            .then(response => response.data);
    },
    {
        optimisticUpdate: (exclusionSection, optimisticData) => {
            return optimisticData.map(x => (x.id === exclusionSection.id ? exclusionSection : x));
        },
    }
);

export const useExclusionSectionDelete = factory.mutate(
    (entity: { id: string; studyId: string }): Promise<RevisionedCommandResponse<CommandResponse>> => {
        return axios
            .delete(`/api/study-designer/studies/${entity.studyId}/exclusion-sections/${entity.id}`)
            .then(response => response.data);
    }
);

export const useExclusionSectionMove = factory.mutate(
    (entity: {
        id: string;
        studyId: string;
        placement: PlacementPayload;
    }): Promise<RevisionedCommandResponse<CommandResponse>> => {
        const { id, studyId, placement } = entity;
        return axios
            .put(`/api/study-designer/studies/${studyId}/exclusion-sections/${id}/actions/move`, placement)
            .then(response => response.data);
    }
);

export const useExclusionSectionLink = factory.mutate(
    (entity: {
        id: string;
        studyId: string;
        linkedSection: Partial<StudySection> & Pick<StudySection, 'id' | 'studyId' | 'entityType'>;
    }): Promise<RevisionedCommandResponse<CommandResponse>> => {
        const { id, studyId, linkedSection } = entity;
        return axios
            .put(`/api/study-designer/studies/${studyId}/exclusion-sections/${id}/actions/link`, linkedSection)
            .then(response => response.data);
    }
);

export const useExclusionSectionRename = factory.mutate(
    (entity: {
        id: string;
        studyId: string;
        renamePayload: RenamePayload;
    }): Promise<RevisionedCommandResponse<CommandResponse>> => {
        const { id, studyId, renamePayload } = entity;
        return axios
            .put(`/api/study-designer/studies/${studyId}/exclusion-sections/${id}/actions/rename`, renamePayload)
            .then(response => response.data);
    }
);
